import { useEffect, useState } from 'react';
import { Box, Input, Text } from '@/components/primitives';
import { ActionButton } from '@/components/common/ActionButton';
import { StyledDialog } from '@/components/dialogs/StyledDialog';
import { claimContactMethod, executeMerge, getMe } from '@/api/auth';
import { parseClaimRefusal } from '@/api/contactMethodClaim';
import { signIn } from '@/auth/authData';
import { queryClient } from '@/api/queryClient';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useFeedbackZone } from '@/components/ui/FeedbackZone';
import { reportMutationError } from '@/api/reportMutationError';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { testIds } from '@/test/testIds';
import { dialogMessages, retryInPhrase } from '@/constants';
import { Icons, Actions, vars } from '@/theme';

/**
 * Cross-user contact-method claim (identity-plan.md §7).
 *
 * Reached when `POST /auth/contact-method/` refuses with 409
 * `method_owned_by_another_account`: the method is VERIFIED on a different
 * account, and since one contact method identifies one person (D1) the two
 * accounts are the same person. That makes this the start of a user merge.
 *
 * Two steps, no branching:
 *   1. Confirm — `POST /users/merge/claim/` creates (or reuses) the UserMerge
 *      and sends a code to EXACTLY the method named.
 *   2. Code — the ordinary `POST /users/merge/{id}/execute/`, the same
 *      verify-then-merge path every other suggestion takes.
 *
 * DELIBERATELY NOT a variant of AccountMergeDialog. That component presents a
 * *discovered* suggestion — pick a method from a list, prove it, merge — and a
 * claim has no list to pick from: `get_verify_methods_for_user` returns []
 * for a provisional claim, precisely so an unproven assertion can't enumerate
 * the owner's methods. The code goes to the address the user just typed. So a
 * claim variant would have to disable the picker, the variant copy, and the
 * Go Back navigation, which is most of what that dialog does.
 *
 * Nothing here names or hints at the other account.
 *
 * Feedback ownership (#821): a dialog component's own `useFeedbackZone()`
 * resolves to its PARENT layer, because StyledDialog's layer provider lives in
 * its render output. So the outer component holds only `pageZone` (the
 * profile dialog's band, which outlives this dialog) for terminal outcomes
 * that close it, and `ClaimDialogBody`, rendered inside the StyledDialog, owns
 * both mutations: anything correctable while the dialog is open (a wrong code,
 * a failed verify or claim) lands in THIS dialog's own band.
 */

export interface ContactMethodClaimDialogProps {
  /** The email or phone the user tried to add. */
  method: string;
  /** vingapi's own opaque refusal wording, shown as-is on the confirm step. */
  refusalMessage: string;
  /** Dismiss without claiming. */
  onCancel: () => void;
  /** The merge executed — the caller should refresh its contact methods. */
  onMerged: () => void | Promise<void>;
}

export function ContactMethodClaimDialog({
  method,
  refusalMessage,
  onCancel,
  onMerged,
}: ContactMethodClaimDialogProps) {
  // Resolved OUTSIDE the StyledDialog → the parent layer (the profile dialog's
  // band), which survives this dialog's unmount.
  const pageZone = useFeedbackZone();
  // Lifted from the body so a pending request can't be dismissed mid-flight.
  const [busy, setBusy] = useState(false);
  const messages = dialogMessages.contactMethodClaim;

  // Terminal claim refusal: nothing inside this dialog can fix it, so it is
  // error-then-close. Push to the surviving zone FIRST, then close.
  const handleRefused = (message: string) => {
    pageZone.push(message, 'error');
    onCancel();
  };

  const handleMerged = async () => {
    pageZone.push(messages.mergeSuccess, 'success');
    await onMerged();
  };

  return (
    <StyledDialog
      open
      onClose={busy ? undefined : onCancel}
      size="compact"
      title={messages.title}
      titleIcon={<Icons.merge style={{ color: vars.color.success.base }} />}
      // The actions live in ClaimDialogBody (inside the dialog layer) so the
      // mutations they drive answer in this dialog's own band (#821).
      footer={null}
      DialogProps={{ 'data-testid': testIds.components.contactMethodClaim.dialog }}
    >
      <ClaimDialogBody
        method={method}
        refusalMessage={refusalMessage}
        onCancel={onCancel}
        onRefused={handleRefused}
        onMerged={handleMerged}
        onBusyChange={setBusy}
      />
    </StyledDialog>
  );
}

/**
 * Rendered INSIDE the StyledDialog, so `useApiMutation` / `useFeedbackZone`
 * here resolve to the claim dialog's own band.
 */
function ClaimDialogBody({
  method,
  refusalMessage,
  onCancel,
  onRefused,
  onMerged,
  onBusyChange,
}: {
  method: string;
  refusalMessage: string;
  onCancel: () => void;
  /** Terminal refusal: the caller pushes to a zone that survives close, then closes. */
  onRefused: (message: string) => void;
  /** The merge executed (identity refreshed, queries invalidated). */
  onMerged: () => Promise<void>;
  onBusyChange: (busy: boolean) => void;
}) {
  const zone = useFeedbackZone();
  const { user } = useCurrentUser();
  const [mergeId, setMergeId] = useState<number | null>(null);
  const [verificationCode, setVerificationCode] = useState('');

  const messages = dialogMessages.contactMethodClaim;

  const claimMutation = useApiMutation({
    mutationFn: () =>
      claimContactMethod(method.includes('@') ? { email: method } : { phone: method }),
    onSuccess: (data) => {
      if (data) setMergeId(data.mergeId);
    },
    // NOTE: supplying onError REPLACES useApiMutation's default handling
    // outright (it early-returns), so `errorMessage` no longer applies and the
    // fallback branch has to be spelled out here.
    onError: (error) => {
      // The two claim-time refusals are outcomes, not failures. `not_claimable`
      // in particular is usually §7's state transition — the owner released
      // the method, so a plain add now succeeds.
      const refusal = parseClaimRefusal(error);
      if (refusal) {
        // Either way there is nothing left to claim here: close, and let them
        // act on the advice (re-add, or wait out the limit). The advice goes to
        // the profile dialog's band, which is still there after this closes.
        // A spent budget names the wait when the server says (#1163).
        onRefused(
          refusal.code === 'not_claimable'
            ? messages.notClaimable
            : messages.rateLimited(retryInPhrase(refusal.retryAfter ?? null)),
        );
        return;
      }
      // 5xx + transport errors are surfaced centrally by routeMutationError,
      // in this dialog's band (#1174).
      reportMutationError(zone, error, messages.claimFailed);
    },
  });

  const executeMutation = useApiMutation({
    mutationFn: (code: string) => {
      if (mergeId === null) throw new Error('claim-not-initiated');
      return executeMerge(mergeId, { code });
    },
    // Execute returns HTTP 200 even for a wrong code — branch on the body,
    // never the status (same contract AccountMergeDialog handles). The wrong-code
    // push carries this mutation's key by hand (#1628-c3): the ambient key is
    // unset after the first await, and zone.push never reads it.
    onSuccess: async (data): Promise<void> => {
      if (!data?.success) {
        zone.push(data?.error || messages.verifyFailed, 'error', { source: executeMutation.feedbackSource });
        return; // keep the dialog open so the user can retry the code
      }
      // The merge changed this user's identity: memberships, level, and their
      // contact methods. Reload /auth/me and write it back, re-attaching the
      // token that /me omits.
      const token = user?.token;
      let switched = false;
      if (token) {
        try {
          const refreshed = await getMe(token);
          switched = user !== null && refreshed.id !== user.id;
          signIn({ ...refreshed, token });
        } catch {
          // Non-fatal — the merge succeeded; stale user data reloads later.
        }
      }
      // When /me came back as the other login, the user switch (#1336) already
      // refreshed every active query once: do not fetch them all twice.
      if (!switched) await queryClient.invalidateQueries();
      try {
        await onMerged();
      } catch {
        // Non-fatal — the merge succeeded and was announced; the caller's
        // refresh failing leaves stale data that reloads later. useApiMutation
        // awaits this onSuccess (#869), so a re-throw would fail the mutation:
        // a false "verify failed" and a crash report for a merge that worked.
      }
    },
    errorMessage: messages.verifyFailed,
  });

  const isLoading = claimMutation.isPending || executeMutation.isPending;
  useEffect(() => {
    onBusyChange(isLoading);
    // A forced unmount mid-request must not leave the outer dialog locked.
    return () => onBusyChange(false);
  }, [isLoading, onBusyChange]);

  const step = mergeId === null ? 'confirm' : 'code';

  const handleVerify = () => {
    const trimmed = verificationCode.trim();
    if (trimmed) executeMutation.mutate(trimmed);
  };

  return (
    <Box>
      {step === 'confirm' ? (
        <Box>
          {/* vingapi's wording, which is already correctly opaque about whose
              account it is — don't restate it in our own words. */}
          <Text color="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {refusalMessage}
          </Text>
          <Text color="secondary" style={{ display: 'block' }}>
            {messages.intro(method)}
          </Text>
        </Box>
      ) : (
        <Box>
          <Text color="secondary" style={{ display: 'block', marginBottom: 24 }}>
            {messages.codeSent(method)}
          </Text>
          <Input
            label="Verification Code"
            placeholder="Enter Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            disabled={isLoading}
            autoFocus
            data-testid={testIds.components.contactMethodClaim.codeInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && verificationCode.trim()) handleVerify();
            }}
          />
        </Box>
      )}
      <Box
        display="flex"
        style={{ justifyContent: 'center', gap: 8, width: '100%', marginTop: vars.spacing.lg }}
      >
        <ActionButton
          action={Actions.cancel}
          mode="dialog-secondary"
          label={messages.cancel}
          onClick={onCancel}
          disabled={isLoading}
          testIdOverride={testIds.components.contactMethodClaim.cancelButton}
        />
        {step === 'confirm' ? (
          <ActionButton
            action={Actions.verify}
            mode="dialog-primary"
            label={messages.claim}
            onClick={() => claimMutation.mutate(undefined)}
            loading={claimMutation.isPending}
            loadingLabel={messages.claim}
            testIdOverride={testIds.components.contactMethodClaim.claimButton}
          />
        ) : (
          <ActionButton
            action={Actions.verify}
            mode="dialog-primary"
            label={messages.verify}
            onClick={handleVerify}
            disabled={!verificationCode.trim()}
            loading={executeMutation.isPending}
            loadingLabel={messages.verify}
            testIdOverride={testIds.components.contactMethodClaim.verifyButton}
          />
        )}
      </Box>
    </Box>
  );
}
